import React, { useEffect, useMemo, useState } from "react";
import FaqTab from "../customer-center/FaqTab";
import BoardPostsTab from "../BoardPostsTab";
import {
  BOARD_GROUPS,
  TAB_VIEW_TYPE,
  getBoardTabs,
} from "../../../services/contactCenterService";
import { getI18nLanguageFromLocalStorage } from "../../../helpers/localStorageHelper";
import { getLocalizedField } from "../../../helpers/localizedHelper";
import { slugify } from "../../../helpers/slugHelper";
import { useTranslation } from "react-i18next";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.INFO_BOARD.EMPTY": "No boards available.",
};

const GROUP = BOARD_GROUPS.INFO_BOARD;

const SUB_TAB_QUERY_PARAM = "subtab";

const getSubTabFromUrl = () => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(SUB_TAB_QUERY_PARAM);
};

/** 정보 게시판 — tabs from tab management (수업현장 / 자료실 / 보도자료). */
const InfoBoard = ({ onOpenPost }) => {
  const [tabs, setTabs] = useState([]);
  const [active, setActive] = useState(null);
  const language = getI18nLanguageFromLocalStorage();
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  useEffect(() => {
    getBoardTabs(GROUP).then((data) => setTabs(data || []));
  }, []);

  const subTabs = useMemo(
    () =>
      [...tabs]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((tb) => ({
          key: `tab-${tb.id}`,
          slug: slugify(tb.nameEn),
          label: getLocalizedField(tb, "name", language),
          tab: tb,
        })),
    [tabs, language]
  );

  // Resolve the active sub-tab once the list is built: prefer ?subtab=<slug>, else the first.
  useEffect(() => {
    if (!subTabs.length || active) return;
    const fromUrl = getSubTabFromUrl();
    const match = subTabs.find((s) => s.slug === fromUrl);
    setActive(match ? match.key : subTabs[0].key);
  }, [subTabs, active]);

  const selectTab = (item) => {
    setActive(item.key);
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    params.set(SUB_TAB_QUERY_PARAM, item.slug);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  };

  const renderActive = () => {
    const found = subTabs.find((s) => s.key === active);
    if (!found) return <div className="cuc-empty">{t("CONTACT_US_CENTER.INFO_BOARD.EMPTY")}</div>;
    const tab = found.tab;
    if (tab.viewType === TAB_VIEW_TYPE.ACCORDION_VIEW) {
      return <FaqTab group={GROUP} tab={tab} />;
    }
    return (
      <BoardPostsTab
        group={GROUP}
        tab={tab}
        defaultView={tab.viewType === TAB_VIEW_TYPE.POST_GALLERY ? "gallery" : "list"}
        trailing="action"
        onOpenPost={onOpenPost}
      />
    );
  };

  return (
    <div className="cuc-panel">
      <div className="cuc-pills">
        {subTabs.map((t) => (
          <button
            key={t.key}
            className={`cuc-pill ${active === t.key ? "is-active" : ""}`}
            onClick={() => selectTab(t)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {renderActive()}
    </div>
  );
};

export default InfoBoard;
